import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Loader2, Plus, Pencil, Trash2, MapPin, Warehouse, Check, X } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const emptyHub = { name: "", city: "", state: "", address: "", lat: "", lng: "", capacity: 500, active: true, pincode_whitelist: "" };

// Sprint-101b — full Indian states + UTs list for Hub creation dropdown.
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

export default function AdminHubs() {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHub, setEditingHub] = useState(null);
  const [form, setForm] = useState(emptyHub);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchHubs = async () => {
    try {
      const { data } = await api.get("/hubs");
      setHubs(data.hubs || []);
    } catch {
      toast.error("Failed to fetch hubs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHubs(); }, []);

  const openCreate = () => {
    setEditingHub(null);
    setForm(emptyHub);
    setDialogOpen(true);
  };

  const openEdit = (hub) => {
    setEditingHub(hub);
    setForm({
      name: hub.name,
      city: hub.city,
      state: hub.state || "",
      address: hub.address,
      lat: hub.lat,
      lng: hub.lng,
      capacity: hub.capacity,
      active: hub.active,
      pincode_whitelist: Array.isArray(hub.pincode_whitelist)
        ? hub.pincode_whitelist.join(", ")
        : (hub.pincode_whitelist || ""),
    });
    setDialogOpen(true);
  };

  // Sprint-101b — optimistic active toggle via PATCH /api/admin/hubs/:id/active
  const handleToggleActive = async (hub) => {
    const id = hub._id || hub.hub_id;
    const prev = hubs;
    const next = hubs.map((h) =>
      (h.hub_id === hub.hub_id || h._id === hub._id)
        ? { ...h, active: !h.active }
        : h
    );
    setHubs(next);
    try {
      await api.patch(`/admin/hubs/${id}/active`, { active: !hub.active });
      toast.success(`Hub ${hub.active ? "deactivated" : "activated"}`);
    } catch (e) {
      setHubs(prev);
      toast.error(
        (typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : null) || "Couldn't update hub status"
      );
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.state || !form.lat || !form.lng) {
      toast.error("Name, city, state, lat, and lng are required");
      return;
    }
    setSaving(true);
    try {
      const wl = (form.pincode_whitelist || "")
        .split(/[,\s]+/).map((s) => s.trim()).filter((s) => /^\d{6}$/.test(s));
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), capacity: parseInt(form.capacity) || 500, pincode_whitelist: wl };
      if (editingHub) {
        await api.put(`/hubs/${editingHub.hub_id}`, payload);
        toast.success("Hub updated");
      } else {
        await api.post("/hubs", payload);
        toast.success("Hub created");
      }
      setDialogOpen(false);
      fetchHubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save hub");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (hubId) => {
    if (!window.confirm("Delete this hub? This cannot be undone.")) return;
    setDeletingId(hubId);
    try {
      await api.delete(`/hubs/${hubId}`);
      toast.success("Hub deleted");
      fetchHubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete hub");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-hubs-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Hub Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage logistics hubs for multi-city operations</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-11 font-semibold shadow-sm shadow-[#FF6B00]/20 btn-press" data-testid="create-hub-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Hub
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fadeInUp" style={{ animationDelay: "0.05s" }}>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Hubs</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-hubs-count">{hubs.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{hubs.filter(h => h.active).length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Capacity</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{hubs.reduce((s, h) => s + (h.capacity || 0), 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Cities</p>
          <p className="text-2xl font-bold text-[#FF6B00] mt-1">{new Set(hubs.map(h => h.city)).size}</p>
        </div>
      </div>

      {/* Hubs Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-fadeInUp" style={{ animationDelay: "0.1s" }}>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-700">Hub ID</TableHead>
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">City</TableHead>
              <TableHead className="font-semibold text-slate-700">State</TableHead>
              <TableHead className="font-semibold text-slate-700">Location</TableHead>
              <TableHead className="font-semibold text-slate-700">Capacity</TableHead>
              <TableHead className="font-semibold text-slate-700">Whitelist</TableHead>
              <TableHead className="font-semibold text-slate-700">Active</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hubs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                  <Warehouse className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No hubs configured yet</p>
                </TableCell>
              </TableRow>
            ) : (
              hubs.map((hub) => (
                <TableRow key={hub.hub_id} data-testid={`hub-row-${hub.hub_id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{hub.hub_id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
                        <Warehouse className="h-4 w-4 text-[#FF6B00]" />
                      </div>
                      <span className="font-medium text-slate-800 text-sm">{hub.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{hub.city}</TableCell>
                  <TableCell className="text-sm text-slate-700" data-testid={`hub-state-${hub.hub_id}`}>
                    {hub.state || <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{hub.lat?.toFixed(4)}, {hub.lng?.toFixed(4)}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-700">{hub.capacity?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-500" data-testid={`hub-whitelist-${hub.hub_id}`}>
                    {Array.isArray(hub.pincode_whitelist) && hub.pincode_whitelist.length > 0
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{hub.pincode_whitelist.length} pin{hub.pincode_whitelist.length > 1 ? "s" : ""}</span>
                      : <span className="text-slate-400">open</span>}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!hub.active}
                      onCheckedChange={() => handleToggleActive(hub)}
                      data-testid={`hub-active-toggle-${hub.hub_id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(hub)} className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600" data-testid={`edit-hub-${hub.hub_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(hub.hub_id)} disabled={deletingId === hub.hub_id} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" data-testid={`delete-hub-${hub.hub_id}`}>
                        {deletingId === hub.hub_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg">{editingHub ? "Edit Hub" : "Add New Hub"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">Hub Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mumbai Central Hub" className="mt-1 rounded-xl" data-testid="hub-name-input" required />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Mumbai" className="mt-1 rounded-xl" data-testid="hub-city-input" required />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500">State <span className="text-red-500">*</span></Label>
              <select
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                data-testid="hub-state-select"
                required
              >
                <option value="">— Select state —</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" className="mt-1 rounded-xl" data-testid="hub-address-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">Latitude</Label>
                <Input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="19.0760" className="mt-1 rounded-xl" data-testid="hub-lat-input" required />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Longitude</Label>
                <Input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="72.8777" className="mt-1 rounded-xl" data-testid="hub-lng-input" required />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Capacity</Label>
                <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1 rounded-xl" data-testid="hub-capacity-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} id="hub-active" className="rounded" data-testid="hub-active-checkbox" />
              <Label htmlFor="hub-active" className="text-sm text-slate-600 cursor-pointer">Active</Label>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500">
                Pincode whitelist <span className="text-slate-400">· Optional · advanced</span>
              </Label>
              <textarea
                value={form.pincode_whitelist}
                onChange={(e) => setForm({ ...form, pincode_whitelist: e.target.value })}
                placeholder="560001, 560002, 560038"
                rows={2}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                data-testid="hub-whitelist-textarea"
              />
              <p className="text-xs text-slate-400 mt-1">
                Leave empty to serve the whole state. Comma-separated 6-digit pincodes.
              </p>
            </div>
            <p className="text-xs text-slate-400 pt-1">
              Changes apply immediately to new serviceability checks.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl font-semibold shadow-sm" data-testid="hub-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingHub ? "Update Hub" : "Create Hub"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
